import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { saveToken } from "../lib/api";
import { useAuth } from "../App";

/**
 * Login page — with Cloudflare Access, user is already authenticated.
 * This page auto-detects the CF Access email and creates/logs in the user.
 * If auto-detection fails, shows a manual email input.
 */
export default function Login() {
  const [status, setStatus] = useState("detecting"); // detecting | manual | loading
  const [email, setEmail] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    autoLogin();
    // eslint-disable-next-line
  }, []);

  const autoLogin = async () => {
    setStatus("detecting");

    // Try CF Access identity
    let detectedEmail = null;
    try {
      const r = await fetch("/cdn-cgi/access/get-identity");
      if (r.ok) {
        const data = await r.json();
        detectedEmail = data.email;
      }
    } catch {}

    if (detectedEmail) {
      await loginWithEmail(detectedEmail);
    } else {
      // CF Access identity not available — show manual input
      setStatus("manual");
    }
  };

  const loginWithEmail = async (emailToUse) => {
    setStatus("loading");
    try {
      // Try signup first (creates user + workspace if new)
      const r = await api.post("/auth/signup", { email: emailToUse, name: emailToUse.split("@")[0] });
      if (r.data.session_token) saveToken(r.data.session_token);
      setUser(r.data);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      if (e?.response?.status === 409) {
        // Already exists — login
        try {
          const r = await api.post("/auth/login-by-email", { email: emailToUse });
          if (r.data.session_token) saveToken(r.data.session_token);
          setUser(r.data);
          navigate("/dashboard", { replace: true });
        } catch {
          toast.error("Gagal masuk. Coba lagi.");
          setStatus("manual");
        }
      } else if (e?.response?.status === 403 && e?.response?.data?.status === "pending") {
        // Access request sent to admin
        setStatus("pending");
        toast.success("Permintaan akses dikirim ke admin!");
      } else {
        toast.error(e?.response?.data?.detail || "Gagal masuk");
        setStatus("manual");
      }
    }
  };

  const handleManualLogin = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    loginWithEmail(email.trim().toLowerCase());
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* LEFT — visual */}
      <div
        className="relative hidden lg:flex flex-col justify-between border-r-2 border-black overflow-hidden"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1777307176207-6ecc9752b715?crop=entropy&cs=srgb&fm=jpg&q=85")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-white/50" />
        <div className="absolute inset-0 bg-grid-strong opacity-40" />
        <div className="relative z-10 p-10 flex flex-col justify-between h-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black flex items-center justify-center">
              <span className="text-white font-mono text-xs">NT</span>
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.25em]">
              NEW / TRELLO / 2026
            </span>
          </div>

          <div className="space-y-6 max-w-md">
            <div className="inline-block bg-black text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1">
              PAPAN KERJA · BAHASA INDONESIA
            </div>
            <h1 className="font-heading font-black text-6xl xl:text-7xl leading-[0.92] tracking-tighter uppercase">
              Kerja
              <br />
              <span className="text-klein">Terstruktur.</span>
              <br />
              Pikiran
              <br />
              <span className="bg-canary px-2">Tenang.</span>
            </h1>
            <p className="font-mono text-sm max-w-sm text-black/70">
              Kanban brutalis untuk tim yang menolak kompromi visual. Drag, tulis, kelola — tanpa kebisingan UI.
            </p>
          </div>

          <div className="flex justify-between items-end font-mono text-[10px] uppercase tracking-widest">
            <div className="space-y-1">
              <div className="opacity-60">SISTEM</div>
              <div>v1.0 · BRUTALIST EDITION</div>
            </div>
            <div className="space-y-1 text-right">
              <div className="opacity-60">PROYEK</div>
              <div>///// 001</div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — login */}
      <div className="relative flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative w-full max-w-md">
          {status === "detecting" || status === "loading" ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-black flex items-center justify-center mx-auto">
                <span className="text-white font-mono text-lg font-bold">NT</span>
              </div>
              <div className="space-y-2">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-black/60">
                  [ AUTENTIKASI ]
                </div>
                <h2 className="font-heading font-black text-3xl tracking-tighter uppercase">
                  Menghubungkan…
                </h2>
              </div>
              <Loader2 size={24} className="animate-spin mx-auto text-klein" strokeWidth={2.5} />
            </div>
          ) : status === "pending" ? (
            <div className="space-y-8">
              <div className="space-y-2">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-black/60">
                  [ MENUNGGU PERSETUJUAN ]
                </div>
                <h2 className="font-heading font-black text-3xl tracking-tighter uppercase">
                  Permintaan
                  <br />
                  Terkirim.
                </h2>
                <p className="font-mono text-sm text-black/70 pt-2">
                  Admin telah menerima permintaan akses Anda via Telegram. Silakan tunggu persetujuan, lalu coba masuk kembali.
                </p>
              </div>
              <div className="border-2 border-canary bg-canary/10 p-4">
                <p className="font-mono text-xs text-black/80">
                  💡 Setelah admin menyetujui, klik tombol di bawah untuk masuk.
                </p>
              </div>
              <button
                onClick={() => { setStatus("manual"); }}
                className="w-full bg-black text-white border-2 border-black px-6 py-4 font-mono uppercase text-sm tracking-wider hover:bg-klein hover:-translate-y-1 hover:shadow-brut transition-all"
              >
                COBA MASUK LAGI
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="space-y-2">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-black/60">
                  [ 01 / MASUK ]
                </div>
                <h2 className="font-heading font-black text-4xl tracking-tighter uppercase">
                  Masuk ke
                  <br />
                  New Trello.
                </h2>
                <p className="font-mono text-sm text-black/70 pt-2">
                  Masukkan email yang Anda gunakan untuk login Cloudflare Access.
                </p>
              </div>

              <form onSubmit={handleManualLogin} className="space-y-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest block mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@gmail.com"
                    autoFocus
                    data-testid="login-email-input"
                    className="w-full bg-white border-2 border-black px-4 py-3 font-mono text-sm focus:shadow-brutBlue transition-all"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="login-submit-btn"
                  className="group w-full bg-black text-white border-2 border-black px-6 py-4 font-mono uppercase text-sm tracking-wider flex items-center justify-between hover:bg-klein hover:-translate-y-1 hover:shadow-brut transition-all"
                >
                  <span>MASUK</span>
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
                </button>
              </form>

              <div className="pt-6 border-t border-black/20 font-mono text-[10px] uppercase tracking-widest text-black/60 leading-loose">
                <div>OTORISASI · CLOUDFLARE ACCESS</div>
                <div>SESI 24 JAM · ZERO TRUST</div>
                <div>DATA · CLOUDFLARE D1 · EDGE</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
